import { AssetUploadStatuses, Template, User, WorkOrder, WorkOrderResult, WorkOrderStatus } from "./models";
import { TemplateQuery, WorkOrderQuery } from "./query-strings";

/** Shared status object used to track API statuses in components. */
export enum SubmitStatus {
  Idle,
  Submitting,
  Submitted,
  Failed
}

/* File to upload.*/
export interface File {
  fileName: string,
  fileData: string | Blob
}

/** Enumeration of HTTP method types. */
enum HttpMethod {
  Get = "GET",
  Post = "POST",
  Put = "PUT",
  Delete = "DELETE"
};

/** Fetch options to use. */
type FetchOptions = {
  bypassUnauthenticatedRedirect?: boolean,
  httpMethod?: HttpMethod,
  isRequestJson?: boolean,
  isRequestText?: boolean,
  isRequestFile?: boolean,
  isRequestAuthorized?: boolean,
  body?: any,
  files?: File[],
  isResponseJson?: boolean,
  isResponseText?: boolean,
  searchParams?: { [key: string]: any }
};

/** Response to an API request. */
export class Response<T> {
  /** Status code of the response. */
  status?: number;

  /** Received and interpreted body. */
  body?: T;

  /** Error message if one was read. */
  error?: String;

  /** Did an error happen. */
  hasError?: boolean;

  constructor(status?: number, body?: T, hasError?: boolean) {
    this.status = status;
    this.body = body;
    this.hasError = hasError;
  }

  good(): boolean {
    if (this.hasError) {
      return false;
    }

    if (!this.status) {
      return false;
    }

    const isGood = this.status > 199 && this.status < 300;
    return isGood;
  }
}

declare var process : {
  env: {
    REACT_APP_WORK_ORDER_API_URI: string
  }
}

/** Work Order API client implementation that talks to a server. */
export class WorkOrderApi {
  private readonly url: string;
  private token?: string;

  constructor(token?: string) {
    this.url = process.env.REACT_APP_WORK_ORDER_API_URI;
    this.token = token;
  }

  /** Determines if a token is stored.
   *  This does not guarantee the token is valid. */
  hasToken() {
    const hasToken = this.token !== undefined;
    return hasToken;
  }

  async login(emailAddress: string, password: string) {
    const loginResponse = await this.fetchWithResponse<string>("authentication", {
      httpMethod: HttpMethod.Post,
      body: { emailAddress, password },
      isRequestJson: true,
      isRequestAuthorized: false,
      isResponseText: true,
      bypassUnauthenticatedRedirect: true
    });
    return loginResponse;
  }

  async postPasswordReset(emailAddress: string) {
    const passwordResetResponse = await this.fetchWithResponse<undefined>("passwordreset", {
      httpMethod: HttpMethod.Post,
      body: emailAddress,
      isRequestJson: true,
      isRequestAuthorized: false
    });
    return passwordResetResponse;
  }

  async putPasswordReset(passwordResetRequestId: number, token: string, newPassword: string) {
    const passwordResetResponse = await this.fetchWithResponse<undefined>("passwordreset/" + passwordResetRequestId, {
      httpMethod: HttpMethod.Put,
      body: { newPassword, token },
      isRequestJson: true,
      isRequestAuthorized: false,
      bypassUnauthenticatedRedirect: true
    });

    return passwordResetResponse;
  }

  async postInvitation(emailAddress: string) {
    const invitationResponse = await this.fetchWithResponse<undefined>("invitation", {
      body: emailAddress,
      httpMethod: HttpMethod.Post,
      isRequestAuthorized: true,
      isRequestJson: true
    });

    return invitationResponse;
  }

  async putInvitation(invitationId: number, token: string, password: string) {
    const invitationResponse = await this.fetchWithResponse<undefined>("invitation/" + invitationId, {
      body: { token, password },
      httpMethod: HttpMethod.Put,
      isRequestAuthorized: true,
      isRequestJson: true
    });

    return invitationResponse;
  }

  async getWorkOrderResultList(workOrderId: number): Promise<Response<WorkOrderResult[]>> {
    const workOrderResults = await this.fetchWithResponse<WorkOrderResult[]>(`workorder/${workOrderId}/result`, {
      isRequestAuthorized: true,
      isResponseJson: true
    });
    return workOrderResults;
  }

  async downloadWorkOrderResult(workOrderId: number, name: string) {
    const fullUrl = [this.url, `workorder/${workOrderId}/result/${encodeURIComponent(name)}`].join("/");
    const blob = await fetch(fullUrl, {
      headers: {
        "Authorization": "Bearer " + this.token
      },
      redirect: "manual"
    }).then(res => res.blob());

    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = name;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(objectUrl);
  }

  async getTemplates(query: TemplateQuery): Promise<Response<Template[]>> {
    const urlSearchParams = new URLSearchParams();
    if (query.deleted !== undefined && query.deleted !== null) {
      urlSearchParams.append("deleted", String(query.deleted));
    }
    if (query.statuses !== undefined && query.statuses !== null) {
      for (const status of query.statuses) {
        urlSearchParams.append("statuses", status);
      }
    }
    if (query.sortBy !== undefined && query.sortBy !== null) {
      urlSearchParams.append("sortBy", query.sortBy);
    }
    if (query.sortDescending !== undefined && query.sortDescending !== null) {
      urlSearchParams.append("sortDescending", String(query.sortDescending));
    }

    let queryString = urlSearchParams.toString();
    queryString = queryString.length > 0 ? `?${queryString}` : "";
    const templates = await this.fetchWithResponse<Template[]>(`template${queryString}`, {
      isRequestAuthorized: true,
      isResponseJson: true
    });
    return templates;
  }

  async getTemplate(templateId: number): Promise<Response<Template>> {
    const template = await this.fetchWithResponse<Template>(`template/${templateId}`, {
      isRequestAuthorized: true,
      isResponseJson: true
    });
    return template;
  }

  /** Creates a template. */
  async postTemplate(template: Template): Promise<Response<Template>> {
    const templateResponse = await this.fetchWithResponse<Template>(`template`, {
      httpMethod: HttpMethod.Post,
      body: template,
      isRequestAuthorized: true,
      isRequestJson: true,
      isResponseJson: true
    });
    return templateResponse;
  }

  /** Updates a template. */
  async putTemplate(template: Template): Promise<Response<Template>> {
    const templateResponse = await this.fetchWithResponse<Template>(`template/${template.templateID}`, {
      httpMethod: HttpMethod.Put,
      body: template,
      isRequestAuthorized: true,
      isRequestJson: true,
      isResponseJson: true
    });
    return templateResponse;
  }

  async deleteTemplate(templateId: number) {
    const response = await this.fetchWithResponse<undefined>(`template/${templateId}`, {
      httpMethod: HttpMethod.Delete,
      isRequestAuthorized: true
    });
    return response;
  }

  async getTemplateAssets(templateId: number): Promise<Response<AssetUploadStatuses>> {
    const assetUploadResponse = await this.fetchWithResponse<AssetUploadStatuses>(`template/${templateId}/asset`, {
      httpMethod: HttpMethod.Get,
      isRequestAuthorized: true,
      isResponseJson: true
    });
    return assetUploadResponse;
  }

  async postTemplateAssets(templateId: Number, files: File[]): Promise<Response<AssetUploadStatuses>> {
    const assetUploadResponse = await this.fetchWithResponse<AssetUploadStatuses>(`template/${templateId}/asset`, {
      httpMethod: HttpMethod.Post,
      files: files,
      isRequestFile: true,
      isRequestAuthorized: true,
      isResponseJson: true
    });
    return assetUploadResponse;
  }

  async postTemplateAssetConfirm(templateId: number): Promise<Response<undefined>> {
    const confirmResponse = await this.fetchWithResponse<undefined>(`template/${templateId}/asset/confirm`, {
      httpMethod: HttpMethod.Post,
      isRequestAuthorized: true
    });
    return confirmResponse;
  }

  async getWorkOrderParameterAssets(workOrderId: number): Promise<Response<AssetUploadStatuses>> {
    const assetUploadResponse = await this.fetchWithResponse<AssetUploadStatuses>(`workorder/${workOrderId}/parameter`, {
      httpMethod: HttpMethod.Get,
      isRequestAuthorized: true,
      isResponseJson: true
    });
    return assetUploadResponse;
  }

  async postWorkOrderParameterAssets(workOrderId: number, files: File[]): Promise<Response<AssetUploadStatuses>> {
    const assetUploadResponse = await this.fetchWithResponse<AssetUploadStatuses>(`workorder/${workOrderId}/parameter`, {
      httpMethod: HttpMethod.Post,
      files: files,
      isRequestFile: true,
      isRequestAuthorized: true,
      isResponseJson: true
    });
    return assetUploadResponse;
  }

  async postWorkOrderParameterConfirm(workOrderId: number): Promise<Response<undefined>> {
    const confirmResponse = await this.fetchWithResponse<undefined>(`workorder/${workOrderId}/parameter/confirm`, {
      httpMethod: HttpMethod.Post,
      isRequestAuthorized: true
    });
    return confirmResponse;
  }

  async getWorkOrderDynamicAssets(workOrderId: number): Promise<Response<AssetUploadStatuses>> {
    const assetUploadResponse = await this.fetchWithResponse<AssetUploadStatuses>(`workorder/${workOrderId}/dynamicasset`, {
      httpMethod: HttpMethod.Get,
      isRequestAuthorized: true,
      isResponseJson: true
    });
    return assetUploadResponse;
  }

  async postWorkOrderDynamicAssets(workOrderId: number, files: File[]): Promise<Response<AssetUploadStatuses>> {
    const assetUploadResponse = await this.fetchWithResponse<AssetUploadStatuses>(`workorder/${workOrderId}/dynamicasset`, {
      httpMethod: HttpMethod.Post,
      files: files,
      isRequestFile: true,
      isRequestAuthorized: true,
      isResponseJson: true
    });
    return assetUploadResponse;
  }

  async postWorkOrderDynamicAssetConfirm(workOrderId: number): Promise<Response<undefined>> {
    const confirmResponse = await this.fetchWithResponse<undefined>(`workorder/${workOrderId}/dynamicasset/confirm`, {
      httpMethod: HttpMethod.Post,
      isRequestAuthorized: true
    });
    return confirmResponse;
  }

  async postTemplateValidation(cavalryScene: any): Promise<Response<Template>> {
    const templateResponse = await this.fetchWithResponse<Template>("templatevalidation", {
      httpMethod: HttpMethod.Post,
      body: cavalryScene,
      isRequestAuthorized: true,
      isRequestJson: true,
      isResponseJson: true
    });
    return templateResponse;
  }

  /** Gets a list of users. */
  async getUsers(): Promise<Response<User[]>> {
    const users = await this.fetchWithResponse<User[]>('user', {
      isRequestAuthorized: true,
      isResponseJson: true
    });
    return users;
  }

  /** Gets the current user. */
  async getSelf() {
    const user = await this.fetchWithResponse<User>('user/self', {
      isRequestAuthorized: true,
      isResponseJson: true
    });
    return user;
  }

  /** Updates the password of the user. */
  async putSelfPassword(currentPassword: string, newPassword: string) {
    const response = await this.fetchWithResponse<undefined>('user/self/password', {
      httpMethod: HttpMethod.Put,
      body: { currentPassword, newPassword },
      isRequestAuthorized: true,
      isRequestJson: true,
      bypassUnauthenticatedRedirect: true
    });
    return response;
  }

  async getWorkOrders(query: WorkOrderQuery): Promise<Response<WorkOrder[]>> {
    const urlSearchParams = new URLSearchParams();
    if (query.statuses !== undefined && query.statuses !== null) {
      for (const status of query.statuses) {
        urlSearchParams.append("statuses", status);
      }
    }

    if (query.excludeStatuses !== undefined && query.excludeStatuses !== null) {
      for (const status of query.excludeStatuses) {
        urlSearchParams.append("excludeStatuses", status);
      }
    }
    if (query.sortBy !== undefined && query.sortBy !== null) {
      urlSearchParams.append("sortBy", query.sortBy);
    }
    if (query.sortDescending !== undefined && query.sortDescending !== null) {
      urlSearchParams.append("sortDescending", String(query.sortDescending));
    }

    let queryString = urlSearchParams.toString();
    queryString = queryString.length > 0 ? `?${queryString}` : "";
    const workOrders = await this.fetchWithResponse<WorkOrder[]>(`workorder${queryString}`, {
      isRequestAuthorized: true,
      isResponseJson: true
    });
    return workOrders;
  }

  async getWorkOrder(workOrderId: number): Promise<Response<WorkOrder>> {
    const workOrder = await this.fetchWithResponse<WorkOrder>(`workorder/${workOrderId}`, {
      isRequestAuthorized: true,
      isResponseJson: true
    });
    return workOrder;
  }

  async postWorkOrder(workOrder: WorkOrder): Promise<Response<WorkOrder>> {
    const workOrderResponse = await this.fetchWithResponse<WorkOrder>(`workorder`, {
      httpMethod: HttpMethod.Post,
      body: workOrder,
      isRequestAuthorized: true,
      isRequestJson: true,
      isResponseJson: true
    });
    return workOrderResponse;
  }

  /** Updates a work order. */
  async putWorkOrder(workOrder: WorkOrder): Promise<Response<WorkOrder>> {
    const workOrderResponse = await this.fetchWithResponse<WorkOrder>(`workorder/${workOrder.workOrderID}`, {
      httpMethod: HttpMethod.Put,
      body: workOrder,
      isRequestAuthorized: true,
      isRequestJson: true,
      isResponseJson: true
    });
    return workOrderResponse;
  }

  async searchWorkOrderStatuses(workOrderId: number, offset?: number, count?: number): Promise<Response<WorkOrderStatus[]>> {
    const workOrderStatuses = await this.fetchWithResponse<WorkOrderStatus[]>(`workorder/${workOrderId}/status`, {
      isRequestAuthorized: true,
      isResponseJson: true,
      searchParams: {
        offset: offset,
        count: count
      }
    });
    return workOrderStatuses;
  }

  async getWorkOrderResults(workOrderId: number, token: String): Promise<Response<Record<string, number>>> {
    const workOrderResults = await this.fetchWithResponse<Record<string, number>>(`workOrder/${workOrderId}/result/a`, {
      bypassUnauthenticatedRedirect: true,
      isRequestAuthorized: false,
      isResponseJson: true,
      searchParams: {
        token
      }
    });

    return workOrderResults;
  }

  async fetchWithResponse<T>(url: string, options?: FetchOptions): Promise<Response<T>> {
    const response = new Response<T>();

    let body = undefined;
    const headers: HeadersInit = {};
    if (options?.isRequestJson) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(options.body);
    } else if (options?.isRequestText) {
      headers["Content-Type"] = "text/plain";
      body = String(options.body);
    } else if (options?.isRequestFile) {
      const formData = new FormData();
      for (const file of options?.files!) {
        formData.append(file.fileName, file.fileData);
      }
      body = formData;
    }

    if (options?.isRequestAuthorized) {
      headers["Authorization"] = "Bearer " + this.token;
    }

    let fullUrl = [this.url, url].join("/");
    if (options?.searchParams) {
      for (const key in options?.searchParams) {
        const isValueNullOrUndefined = options?.searchParams[key] === undefined || options?.searchParams[key] === null;
        if (isValueNullOrUndefined) {
          delete options?.searchParams[key];
        }
      }

      const urlSearchParams = new URLSearchParams(options?.searchParams);
      fullUrl = `${fullUrl}?${urlSearchParams.toString()}`
    }

    try {
      const fetchResponse = await fetch(fullUrl, {
        method: options?.httpMethod ?? HttpMethod.Get,
        body: body,
        headers: headers
      });

      if (!fetchResponse) {
        response.hasError = true;
        return response;
      }

      response.status = fetchResponse.status;
      if (!response.good()) {
        // TODO: Figure out how to do this using the navigate object.
        if (response.status === 401 && !options?.bypassUnauthenticatedRedirect) {
          localStorage.removeItem("token");
          window.location.href = "/login";
        }

        response.error = await fetchResponse.text();
        return response;
      }

      if (options?.isResponseText) {
        response.body = await fetchResponse.text() as T;
      } else if (options?.isResponseJson) {
        response.body = await fetchResponse.json() as T;
      }

      return response;
    } catch (err) {
      response.hasError = true;
      return response;
    }
  }
}